import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Lightbulb, LightbulbOff } from 'lucide-react';
import { HeroMediaSlider } from '@/components/home/HeroMediaSlider';
import { HomeHeroData } from '@/lib/cms-hero';
import { trackClick } from '@/lib/analytics';
import { useLanguage } from '@/hooks/use-language';
import { getPagePath, type Lang } from '@/lib/localizedRoutes';

interface HeroSectionProps {
  heroData: HomeHeroData;
  heroMedia: HomeHeroData['media'];
  loading?: boolean;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ heroData, heroMedia, loading }) => {
  const { t, language } = useLanguage();
  const [isHovering, setIsHovering] = useState(false);
  const [mobileFlashlightOn, setMobileFlashlightOn] = useState(false);

  // ─── Flashlight effect — zero forced-reflow path ──────────────────────────
  // Previously every mousemove called getBoundingClientRect() (forced layout
  // read) + setState → React re-render → CSS mask-image recalculated. At 60Hz
  // that was 60 forced layouts/sec and a measurable contribution to
  // PageSpeed's "Forced reflow" metric (~298 ms in the report).
  //
  // New path:
  //  • cache rect once on mount + on resize (no per-event getBoundingClientRect),
  //  • update CSS custom properties --mx / --my directly on the DOM (no React),
  //  • CSS mask-image reads those vars via var(--mx), so the browser composites
  //    without touching React or causing a layout recalculation.
  const sectionRef = useRef<HTMLElement | null>(null);
  const flashlightRef = useRef<HTMLDivElement | null>(null);
  const rectRef = useRef<{ left: number; top: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  const pendingPosRef = useRef<{ x: number; y: number } | null>(null);

  const refreshRect = useCallback(() => {
    if (!sectionRef.current) return;
    const r = sectionRef.current.getBoundingClientRect();
    rectRef.current = { left: r.left, top: r.top };
  }, []);

  useEffect(() => {
    refreshRect();
    window.addEventListener('resize', refreshRect, { passive: true });
    window.addEventListener('scroll', refreshRect, { passive: true });
    return () => {
      window.removeEventListener('resize', refreshRect);
      window.removeEventListener('scroll', refreshRect);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [refreshRect]);

  const applyPos = useCallback(() => {
    rafRef.current = null;
    const p = pendingPosRef.current;
    if (!p || !flashlightRef.current) return;
    flashlightRef.current.style.setProperty('--mx', `${p.x}px`);
    flashlightRef.current.style.setProperty('--my', `${p.y}px`);
  }, []);

  const schedulePos = useCallback((clientX: number, clientY: number) => {
    if (!rectRef.current) return;
    pendingPosRef.current = {
      x: clientX - rectRef.current.left,
      y: clientY - rectRef.current.top,
    };
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(applyPos);
    }
  }, [applyPos]);

  const radius = mobileFlashlightOn ? '150px' : '250px';
  const maskStyle = `radial-gradient(circle ${radius} at var(--mx, 0px) var(--my, 0px), black 0%, transparent 100%)`;

  return (
    <section
      ref={sectionRef}
      className="hero-section relative min-h-[80vh] lg:min-h-[100dvh] flex items-center mirror-style overflow-hidden py-20 lg:py-0"
      onMouseMove={(e) => schedulePos(e.clientX, e.clientY)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onTouchMove={(e) => {
        if (!mobileFlashlightOn) return;
        const touch = e.touches[0];
        if (touch) schedulePos(touch.clientX, touch.clientY);
      }}
      onTouchStart={(e) => {
        if (!mobileFlashlightOn) return;
        const touch = e.touches[0];
        if (touch) schedulePos(touch.clientX, touch.clientY);
      }}
    >
      <div className="grid-cells" />
      <div className="absolute inset-0 z-0">
        {heroMedia.length > 0 ? (
          <>
            <HeroMediaSlider
              items={heroMedia}
              className="opacity-30 lg:opacity-40 grayscale"
            />
            <div
              ref={flashlightRef}
              className={`absolute inset-0 w-full h-full transition-opacity duration-300 pointer-events-none ${mobileFlashlightOn ? 'opacity-100' : 'opacity-0 lg:opacity-0'}`}
              style={{
                opacity: (isHovering || mobileFlashlightOn) ? 1 : 0,
                backgroundImage: heroMedia[0]?.url ? `url(${heroMedia[0].url})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                WebkitMaskImage: maskStyle,
                maskImage: maskStyle,
              }}
            />
          </>
        ) : (
          // No media available (still loading or empty) — neutral background, no fallback image flash
          <div className="w-full h-full bg-background" />
        )}

        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-transparent to-background"></div>
      </div>

      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-3xl"
        >
          {heroData.eyebrow?.trim() && (
            <div className="flex items-center gap-4">
              <div className="h-[1px] w-8 lg:w-12 bg-primary"></div>
              <span className="text-primary uppercase tracking-[0.3em] font-bold text-[10px] lg:text-sm">
                {heroData.eyebrow}
              </span>
            </div>
          )}
          {heroData.title?.trim() && (
            <h1
              className="font-extrabold uppercase tracking-tighter leading-[0.95] lg:leading-[0.9] text-[2.5rem] sm:text-[3.5rem] lg:text-[7.5rem]"
              dangerouslySetInnerHTML={{ __html: heroData.title }}
            />
          )}
          {heroData.subtitle?.trim() && (
            <p className="text-white/60 max-w-xl leading-relaxed text-sm lg:text-base">
              {heroData.subtitle}
            </p>
          )}
          {(heroData.cta1Text?.trim() || heroData.cta2Text?.trim()) && (
            <div className="hero-buttons hidden sm:flex flex-col sm:flex-row">
              {heroData.cta1Text?.trim() && (
                <Link
                  to={heroData.cta1Url || `/${language}/catalog`}
                  className="mirror-button text-center hero-cta-button"
                  onClick={() => trackClick('hero-cta1')}
                >
                  {heroData.cta1Text}
                </Link>
              )}
              {heroData.cta2Text?.trim() && (
                <Link
                  to={heroData.cta2Url || getPagePath('about', language as Lang)}
                  className="mirror-button-outline text-center hero-cta-button"
                  onClick={() => trackClick('hero-cta2')}
                >
                  {heroData.cta2Text}
                </Link>
              )}
            </div>
          )}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 hidden lg:flex"
      >
        <span className="text-[10px] uppercase tracking-widest text-white/30">{t('home.scroll_down')}</span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-primary to-transparent"></div>
      </motion.div>

      {/* Mobile Flashlight Toggle Button */}
      <div className="absolute bottom-6 right-6 lg:hidden z-50">
        <button
          onClick={() => setMobileFlashlightOn(!mobileFlashlightOn)}
          className={`p-4 rounded-full backdrop-blur-md border border-white/10 transition-all duration-300 ${mobileFlashlightOn ? 'bg-primary text-white shadow-[0_0_20px_rgba(176,138,90,0.5)]' : 'bg-background/80 text-white/50'}`}
          aria-label={mobileFlashlightOn ? 'Turn flashlight off' : 'Turn flashlight on'}
          aria-pressed={mobileFlashlightOn}
        >
          {mobileFlashlightOn ? <Lightbulb size={24} /> : <LightbulbOff size={24} />}
        </button>
      </div>
    </section>
  );
};